import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CreditCard, Check, Lock, Info } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { shippingZones, shippingMethods } from "../mock";
import API from "../api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const FREE = 399;

const Checkout = () => {
  const { t, tl, cart, cartTotal, clearCart, itemUnitPrice } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [destId, setDestId] = useState("fr");
  const [carrierId, setCarrierId] = useState("colissimo");
  const [method, setMethod] = useState("card");
  const [card, setCard] = useState({ number: "", name: "", exp: "", cvc: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const zone = shippingZones.find((z) => z.id === destId) || shippingZones[0];
  const carrier = shippingMethods.find((m) => m.id === carrierId) || shippingMethods[0];
  const shipping = cartTotal === 0 || cartTotal >= FREE ? 0 : carrier.rate;
  const total = cartTotal + shipping;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center fade-up">
        <p className="text-[#555] mb-6">{t("loginRequired")}</p>
        <Link to="/auth" className="inline-block bg-[#1b1b1b] text-white px-7 py-3.5 text-sm tracking-wide hover:bg-[#b08d57] transition-colors">{t("signIn")}</Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center fade-up">
        <div className="h-16 w-16 rounded-full bg-[#b08d57]/15 flex items-center justify-center mx-auto mb-6">
          <Check className="h-8 w-8 text-[#b08d57]" strokeWidth={1.5} />
        </div>
        <h1 className="font-serif-lux text-3xl md:text-4xl mb-3">{t("orderPlaced")}</h1>
        <p className="text-sm text-[#555] mb-6">{t("orderNumber")} #{done.id.slice(0, 8).toUpperCase()} — {done.total.toFixed(2)} €</p>
        <div className="flex gap-3 justify-center">
          <Link to="/account" className="bg-[#1b1b1b] text-white px-6 py-3 text-sm tracking-wide hover:bg-[#b08d57] transition-colors">{t("myOrders")}</Link>
          <Link to="/c/all" className="border border-[#1b1b1b] px-6 py-3 text-sm tracking-wide hover:bg-[#1b1b1b] hover:text-white transition-colors">{t("continueShopping")}</Link>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center fade-up">
        <p className="text-[#555] mb-6">{t("cartEmpty")}</p>
        <Link to="/c/all" className="inline-block bg-[#1b1b1b] text-white px-7 py-3.5 text-sm tracking-wide hover:bg-[#b08d57] transition-colors">{t("continueShopping")}</Link>
      </div>
    );
  }

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const items = cart.map((i) => ({ product_id: i.id, qty: i.qty, size: i.size, color: i.color, flavor: i.flavor }));
      const r = await API.post("/payments/checkout", { items, destination: destId, shipping_method: carrierId, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Le paiement a échoué.");
      setBusy(false);
    }
  };

  const inputCls = "w-full px-4 py-3 bg-white border border-border rounded-sm text-sm outline-none focus:border-[#b08d57] transition-colors";
  const methods = [{ id: "card", label: t("card") }, { id: "paypal", label: t("paypal") }, { id: "other", label: t("otherPay") }];

  return (
    <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-10 fade-up">
      <div className="h-px w-10 bg-[#b08d57] mb-4" />
      <h1 className="font-serif-lux text-4xl md:text-5xl mb-10">{t("checkoutTitle")}</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-10">
        <div>
          <div className="mb-6">
            <label className="text-sm block mb-2">{t("destination")}</label>
            <Select value={destId} onValueChange={setDestId}>
              <SelectTrigger className="w-full bg-white rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {shippingZones.map((z) => (<SelectItem key={z.id} value={z.id}>{tl(z.name)}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div className="mb-6">
            <label className="text-sm block mb-2">{t("shippingMethodLabel")}</label>
            <div className="space-y-2.5" data-testid="shipping-methods">
              {shippingMethods.map((m) => {
                const active = carrierId === m.id;
                const isFree = cartTotal >= FREE;
                return (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => setCarrierId(m.id)}
                    data-testid={`shipping-method-${m.id}`}
                    className={`w-full flex items-center justify-between gap-3 text-left border rounded-sm px-4 py-3 transition-all ${active ? "border-[#1b1b1b] bg-[#faf8f5]" : "border-border hover:border-[#1b1b1b]"}`}
                  >
                    <span className="flex items-center gap-3">
                      <span className={`h-4 w-4 rounded-full border flex items-center justify-center ${active ? "border-[#b08d57]" : "border-black/25"}`}>
                        {active && <span className="h-2 w-2 rounded-full bg-[#b08d57]" />}
                      </span>
                      <span>
                        <span className="block text-sm text-[#1b1b1b]">{tl(m.name)}</span>
                        <span className="block text-xs text-muted-foreground">{tl(m.delay)}</span>
                      </span>
                    </span>
                    <span className={`text-sm ${isFree ? "text-[#2f7d4f]" : "text-[#1b1b1b]"}`}>{isFree ? t("free") : `${m.rate.toFixed(2)} €`}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-2">{t("freeShippingNote")}</p>
          </div>

          <label className="text-sm block mb-2">{t("paymentMethod")}</label>
          <form onSubmit={pay} className="space-y-4">
            <div className="flex items-start gap-3 bg-[#f2ede4] rounded-sm p-4 text-sm text-[#555]">
              <CreditCard className="h-5 w-5 flex-shrink-0 text-[#b08d57]" strokeWidth={1.5} />
              <span>Paiement sécurisé par <strong>Stripe</strong>. Vous serez redirigé pour régler votre commande par <strong>Carte bancaire</strong> ou <strong>PayPal</strong>.</span>
            </div>
            {error && <p className="text-sm text-[#8b1e3f]">{error}</p>}
            <button type="submit" data-testid="pay-now" disabled={busy}
              className="w-full bg-[#1b1b1b] text-white py-3.5 text-sm tracking-[0.1em] uppercase flex items-center justify-center gap-2 hover:bg-[#b08d57] transition-colors disabled:opacity-60">
              <Lock className="h-4 w-4" strokeWidth={1.5} /> {t("payNow")} · {total.toFixed(2)} €
            </button>
          </form>
        </div>

        <div className="lg:sticky lg:top-28 self-start bg-white border border-border rounded-sm p-6 h-fit">
          <h2 className="font-serif-lux text-2xl mb-5">{t("total")}</h2>
          <div className="space-y-3 max-h-64 overflow-auto pr-1 mb-4">
            {cart.map((i) => (
              <div key={i.key} className="flex gap-3">
                <img src={i.image} alt="" className="w-12 h-16 object-cover rounded-sm" />
                <div className="flex-1 text-sm">
                  <p className="line-clamp-1">{tl(i.name)}</p>
                  <p className="text-xs text-muted-foreground">x{i.qty}{i.size ? ` · ${i.size}` : ""}{i.flavor ? ` · ${i.flavor}` : ""}</p>
                </div>
                <span className="text-sm">{(itemUnitPrice(i) * i.qty).toFixed(0)} €</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm border-t border-border pt-3">
            <div className="flex justify-between text-[#444]"><span>{t("subtotal")}</span><span>{cartTotal.toFixed(0)} €</span></div>
            <div className="flex justify-between text-[#444]"><span>{t("shipping")} · {tl(carrier.name)}</span><span>{shipping === 0 ? t("free") : `${shipping.toFixed(2)} €`}</span></div>
            <div className="flex justify-between text-lg font-medium border-t border-border pt-2"><span>{t("total")}</span><span>{total.toFixed(2)} €</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
